import React, {useEffect, useMemo, useState} from "react";
import * as MuiIcons from "@mui/icons-material";
import {Menu, MenuItem} from "@mui/material";
import {getPicto} from "../../../../utils/pictoCatalog.js";
import {toastHelper} from "../../../../utils/toast-helper.js";
import ModalDelete from "../../layout/ModalDelete.js";
import {useGetActesQuery} from "../../../../modules/actes/actes.api.js";
import {
    useGetByFormQuery,
    useCreateFormActeMutation,
    useUpdateFormActeMutation,
    useDeleteFormActeMutation,
} from "../../../../modules/formActes/formActes.api.js";
import {Field} from "formik";
import {
    ActeNumber, HeaderIconButton, SaisieSegment, Stepper, UI, cardBaseSx,
} from "./FormUiKit.js";

/**
 * Cartes « actes complémentaires » (refonte handoff FormNewOrEdit v2).
 *
 * Deux modes :
 *  - Édition (formIri) : mutations live contre /form_actes (RTK Query), comme avant.
 *  - Création (formIri null) : les actes sont STAGÉS localement (props staged /
 *    onStagedChange) et envoyés par le parent après le POST du form — l'UI est
 *    identique dans les deux modes (« Enregistrez d'abord » supprimé).
 *
 * L'acte principal LAVAGE n'apparaît jamais ici (carte dédiée ActeLavageCard) ;
 * il est garanti côté serveur (FormProcessor).
 *
 * Le stepper « Photos si “Pb” » des actes CHECKLIST pilote le champ Formik
 * form-level `minPhotoMaintenance` (mapping produit actuel).
 */

function Picto({pictoKey, color}) {
    const entry = getPicto(pictoKey);
    const Cmp = MuiIcons[entry?.mui] || MuiIcons.Help;
    return <Cmp sx={{color: color || "inherit", fontSize: 16}}/>;
}

function natureChipLabel(acte) {
    if (acte?.nature === "CHECKLIST") {
        const n = (acte.elements || []).length;
        return n > 0 ? `Checklist · ${n} points` : "Checklist";
    }
    if (acte?.nature === "BOOLEEN") return "Question Oui / Non";
    return acte?.nature || "—";
}

function ActeCard({row, index, count, hovered, onHover, onMove, onDelete, onToggleObligatoire, pending, maintenanceStepper}) {
    const acte = row.acteObj || {};
    return (
        <div
            onMouseEnter={() => onHover?.(row.key)}
            onMouseLeave={() => onHover?.(null)}
            style={{...cardBaseSx(hovered), opacity: pending ? .65 : 1}}
        >
            {/* Header blanc */}
            <div style={{display: "flex", alignItems: "center", gap: 10, padding: "12px 20px", background: "#fff", borderBottom: `1px solid ${UI.hairlineSoft}`, flexWrap: "wrap"}}>
                <ActeNumber n={index + 2}/>
                <Picto pictoKey={acte.picto} color={UI.navy}/>
                <span style={{fontSize: 14, fontWeight: 700, color: UI.navy, letterSpacing: ".04em"}}>
                    {(acte.libelleFr || row.acteIri || "").toUpperCase()}
                </span>
                <span style={{fontSize: 11, fontWeight: 600, color: UI.textSoft, background: UI.hairlineSoft, padding: "3px 10px", borderRadius: 999}}>
                    {natureChipLabel(acte)}
                </span>
                {acte.modeOperatoire && (
                    <span style={{fontSize: 11, fontWeight: 600, color: UI.navy, background: UI.greenLight, padding: "3px 10px", borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 4}}>
                        <MuiIcons.MenuBook sx={{fontSize: 12}}/>Guide lié
                    </span>
                )}
                <span style={{marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4}}>
                    <HeaderIconButton title="Monter" disabled={pending || index === 0} onClick={() => onMove(index, -1)}>
                        <MuiIcons.KeyboardArrowUp sx={{fontSize: 18}}/>
                    </HeaderIconButton>
                    <HeaderIconButton title="Descendre" disabled={pending || index === count - 1} onClick={() => onMove(index, 1)}>
                        <MuiIcons.KeyboardArrowDown sx={{fontSize: 18}}/>
                    </HeaderIconButton>
                    <HeaderIconButton title="Retirer du formulaire" danger disabled={pending} onClick={() => onDelete(row)}>
                        <MuiIcons.DeleteOutline sx={{fontSize: 17}}/>
                    </HeaderIconButton>
                </span>
            </div>

            {/* Corps : une ligne de pilotage */}
            <div style={{padding: "12px 24px 14px", display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap"}}>
                <div style={{flex: 1, display: "flex", alignItems: "center", gap: 14, minWidth: 260}}>
                    <span style={{fontSize: 13, fontWeight: 600, color: UI.textSoft}}>Saisie</span>
                    <SaisieSegment obligatoire={row.obligatoire} saving={pending}
                                   onChange={(v) => onToggleObligatoire(row, v)}/>
                </div>
                {acte.nature === "CHECKLIST" && maintenanceStepper && (
                    <div style={{display: "flex", alignItems: "center", gap: 10, borderLeft: `1px solid ${UI.hairlineSoft}`, paddingLeft: 24}}>
                        <span style={{fontSize: 13, fontWeight: 600, color: UI.textSoft}}>Photos si « Pb »</span>
                        {maintenanceStepper}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function FormActesField({formIri, onChange, hoveredActe, onHover, staged = [], onStagedChange}) {
    const isStaged = !formIri;
    const {data: catalogue = []} = useGetActesQuery();
    const {data: formActes = []} = useGetByFormQuery(formIri, {skip: !formIri});
    const [createFormActe] = useCreateFormActeMutation();
    const [updateFormActe] = useUpdateFormActeMutation();
    const [deleteFormActe] = useDeleteFormActeMutation();

    const [addAnchor, setAddAnchor] = useState(null);
    const [toDelete, setToDelete] = useState(null);

    const acteByIri = useMemo(() => {
        const m = {};
        catalogue.forEach((a) => { m[a["@id"]] = a; });
        return m;
    }, [catalogue]);

    // Lignes unifiées {key, acteIri, acteObj, ordre, obligatoire, id?} hors LAVAGE.
    const rows = useMemo(() => {
        if (isStaged) {
            return staged.map((s, i) => ({
                key: s.acte["@id"], acteIri: s.acte["@id"], acteObj: s.acte,
                ordre: i + 1, obligatoire: s.obligatoire,
            }));
        }
        return [...formActes]
            .map((fa) => {
                const acteIri = typeof fa.acte === "string" ? fa.acte : fa.acte?.["@id"];
                const acteObj = acteByIri[acteIri] || (typeof fa.acte === "object" ? fa.acte : null);
                return {...fa, key: acteIri, acteIri, acteObj};
            })
            .filter((r) => r.acteObj?.nature !== "LAVAGE")
            .sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
    }, [isStaged, staged, formActes, acteByIri]);

    const available = useMemo(() => {
        const used = new Set(rows.map((r) => r.acteIri));
        return [...catalogue]
            .filter((a) => !used.has(a["@id"]) && a.nature !== "LAVAGE")
            .sort((a, b) => (a.libelleFr || "").localeCompare(b.libelleFr || ""));
    }, [catalogue, rows]);

    // Aperçu mobile : liste enrichie {acte, ordre, obligatoire}.
    const previewKey = useMemo(
        () => rows.map((r) => `${r.acteIri}:${r.ordre}:${r.obligatoire}`).join("|"),
        [rows]
    );
    useEffect(() => {
        onChange?.(rows.map((r) => ({acte: r.acteObj || r.acteIri, ordre: r.ordre, obligatoire: r.obligatoire})));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewKey]);

    /* ---- Actions (staged vs live) ---- */

    const handleAdd = async (acte) => {
        setAddAnchor(null);
        if (isStaged) {
            onStagedChange?.([...staged, {acte, obligatoire: true}]);
            return;
        }
        const nextOrdre = rows.length ? Math.max(...rows.map((r) => r.ordre || 0)) + 1 : 1;
        try {
            await createFormActe({formIri, form: formIri, acte: acte["@id"], ordre: nextOrdre, obligatoire: true}).unwrap();
            toastHelper.displaySuccess("Acte ajouté au formulaire.");
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue.");
        }
    };

    const handleToggleObligatoire = async (row, value) => {
        if (value === row.obligatoire) return;
        if (isStaged) {
            onStagedChange?.(staged.map((s) => s.acte["@id"] === row.acteIri ? {...s, obligatoire: value} : s));
            return;
        }
        if (!row.id) return; // ligne optimiste en attente d'id serveur
        try {
            await updateFormActe({id: row.id, formIri, obligatoire: value}).unwrap();
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue.");
        }
    };

    const handleMove = async (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= rows.length) return;
        if (isStaged) {
            const next = [...staged];
            [next[index], next[target]] = [next[target], next[index]];
            onStagedChange?.(next);
            return;
        }
        const a = rows[index];
        const b = rows[target];
        if (!a.id || !b.id) return; // ligne optimiste en attente d'id serveur
        try {
            // Les deux PATCH en PARALLÈLE : leurs deux patchs optimistes s'appliquent
            // immédiatement (swap complet au clic). En séquentiel, le 2e ordre
            // n'était patché qu'au retour API du 1er → tri instable, move "lent"
            // dans un sens.
            await Promise.all([
                updateFormActe({id: a.id, formIri, ordre: b.ordre}).unwrap(),
                updateFormActe({id: b.id, formIri, ordre: a.ordre}).unwrap(),
            ]);
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue lors du réordonnancement.");
        }
    };

    const confirmDelete = async () => {
        const row = toDelete;
        setToDelete(null);
        if (!row) return;
        if (isStaged) {
            onStagedChange?.(staged.filter((s) => s.acte["@id"] !== row.acteIri));
            return;
        }
        if (!row.id) return; // ligne optimiste en attente d'id serveur
        try {
            await deleteFormActe({id: row.id, formIri}).unwrap();
            toastHelper.displaySuccess("Acte retiré du formulaire.");
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue.");
        }
    };

    /* ---- Rendu ---- */

    return (
        <>
            {rows.map((row, index) => (
                <ActeCard
                    key={row.key}
                    row={row}
                    index={index}
                    count={rows.length}
                    hovered={hoveredActe === row.key}
                    onHover={onHover}
                    onMove={handleMove}
                    onDelete={setToDelete}
                    onToggleObligatoire={handleToggleObligatoire}
                    pending={Boolean(row._pending)}
                    maintenanceStepper={
                        row.acteObj?.nature === "CHECKLIST" ? (
                            <Field name="minPhotoMaintenance">
                                {({form, field}) => (
                                    <Stepper
                                        value={field.value ?? 0}
                                        onChange={(v) => form.setFieldValue("minPhotoMaintenance", v)}
                                    />
                                )}
                            </Field>
                        ) : null
                    }
                />
            ))}

            {/* Ajouter un acte */}
            <div
                onClick={(e) => available.length > 0 && setAddAnchor(e.currentTarget)}
                style={{
                    border: `1.5px dashed ${UI.fieldBorder}`, borderRadius: 14, padding: "16px 20px",
                    display: "flex", alignItems: "center", gap: 12,
                    cursor: available.length > 0 ? "pointer" : "default", transition: "all .2s",
                    opacity: available.length > 0 ? 1 : .6,
                }}
            >
                <span style={{width: 30, height: 30, borderRadius: "50%", background: UI.green, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700}}>+</span>
                <div>
                    <div style={{fontSize: 14, fontWeight: 700, color: UI.navy}}>Ajouter un acte complémentaire</div>
                    <div style={{fontSize: 12, color: UI.textMuted}}>
                        Depuis le catalogue — les nouveaux actes sont enregistrés avec le formulaire, même à la création.
                    </div>
                </div>
                <span style={{marginLeft: "auto", fontSize: 12, color: UI.textMuted}}>
                    {available.length > 0 ? `${available.length} acte${available.length > 1 ? "s" : ""} disponible${available.length > 1 ? "s" : ""}` : "Catalogue épuisé"}
                </span>
            </div>
            <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
                {available.map((a) => (
                    <MenuItem key={a["@id"]} onClick={() => handleAdd(a)}>
                        <span style={{display: "inline-flex", alignItems: "center", gap: 8}}>
                            <Picto pictoKey={a.picto} color={UI.navy}/>{a.libelleFr}
                        </span>
                    </MenuItem>
                ))}
            </Menu>

            <ModalDelete
                open={Boolean(toDelete)}
                onClose={() => setToDelete(null)}
                onDelete={confirmDelete}
                label="Retirer cet acte du formulaire :"
                objectToDelete={toDelete?.acteObj?.libelleFr || ""}
            />
        </>
    );
}
